import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";
import {
  ChartLineUp, Users, Package, Receipt, HardDrives, Broadcast,
  MapPin, Lifebuoy, SignOut, WifiHigh, Bell, CellTower, List, X, UsersFour, Buildings, Globe, ChartBar, Key, ShieldStar,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import ThemeToggle from "@/components/ThemeToggle";
import HealthStrip from "@/components/HealthStrip";

// scope: "master" = only super admin, "nas_admin" = super admin + NAS admin,
// "any"  = every signed-in user (super, NAS admin, NAS employee)
const nav = [
  { to: "/", label: "Dashboard", icon: ChartLineUp, testid: "nav-dashboard", end: true, scope: "any" },
  { to: "/subscribers", label: "Subscribers", icon: Users, testid: "nav-subscribers", scope: "any" },
  { to: "/plans", label: "Plans", icon: Package, testid: "nav-plans", scope: "any" },
  { to: "/ip-pools", label: "IP Pools", icon: Globe, testid: "nav-ip-pools", scope: "any" },
  { to: "/billing", label: "Billing", icon: Receipt, testid: "nav-billing", scope: "any" },
  { to: "/olts", label: "OLT Inventory", icon: HardDrives, testid: "nav-olts", scope: "any" },
  { to: "/olt-dashboard", label: "OLT Dashboard", icon: ChartBar, testid: "nav-olt-dashboard", scope: "any" },
  { to: "/nas", label: "NAS Devices", icon: CellTower, testid: "nav-nas", scope: "master" },
  { to: "/nas-logins", label: "NAS Logins", icon: Key, testid: "nav-nas-logins", scope: "master" },
  { to: "/nas-staff", label: "NAS Employees", icon: UsersFour, testid: "nav-nas-staff", scope: "nas_admin" },
  { to: "/roles", label: "Roles", icon: ShieldStar, testid: "nav-roles", scope: "nas_admin" },
  { to: "/radius", label: "RADIUS Logs", icon: Broadcast, testid: "nav-radius", scope: "any" },
  { to: "/reports", label: "Reports", icon: ChartBar, testid: "nav-reports", scope: "any" },
  { to: "/logs", label: "CTS Log Server", icon: Broadcast, testid: "nav-logs", scope: "any" },
  { to: "/nat-logs", label: "CGNAT Logs", icon: ShieldStar, testid: "nav-nat-logs", scope: "any" },
  { to: "/map", label: "Client Map", icon: MapPin, testid: "nav-map", scope: "any" },
  { to: "/support", label: "Support", icon: Lifebuoy, testid: "nav-support", scope: "any" },
  { to: "/company", label: "Company Profile", icon: Buildings, testid: "nav-company", scope: "master" },
];

// Normalize whichever shape /auth/me or /staff/login returns.
// - master admin: type=master (or unset) & role in {admin, master_admin}
// - NAS admin  : type=staff & (role === "nas_admin" || role === "nas-nas_admin")
// - NAS staff  : type=staff & anything else
function classify(user) {
  if (!user) return "none";
  const isStaff = user.type === "staff" || user.is_staff === true || (user.role || "").startsWith("nas-");
  if (!isStaff) return "master";
  const raw = (user.role || "").replace(/^nas-/, "");
  return raw === "nas_admin" ? "nas_admin" : "nas_staff";
}

function canSee(item, kind) {
  if (kind === "master") return true;
  if (kind === "nas_admin") return item.scope !== "master";
  if (kind === "nas_staff") return item.scope === "any";
  return false;
}

export default function Layout() {
  const { user, logout, changePassword } = useAuth();
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [tenantNas, setTenantNas] = useState(null);
  // Staff signs in scoped to exactly one NAS — resolve its name/location once
  // and pin it to the top-bar so franchise operators always know their scope.
  useEffect(() => {
    const kind = classify(user);
    if (kind !== "nas_admin" && kind !== "nas_staff") { setTenantNas(null); return; }
    let alive = true;
    http.get("/nas").then(({ data }) => { if (alive && Array.isArray(data) && data[0]) setTenantNas(data[0]); }).catch(() => {});
    return () => { alive = false; };
  }, [user?.id, user?.nas_id]);
  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 8) return toast.error("New password must be at least 8 characters");
    if (pwd.next !== pwd.confirm) return toast.error("Passwords do not match");
    setPwdSaving(true);
    try {
      await changePassword(pwd.current, pwd.next);
      toast.success("Password changed. Please sign in again on other devices.");
      setPwdOpen(false); setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Change failed");
    } finally { setPwdSaving(false); }
  };

  const close = () => setDrawer(false);

  return (
    <div className="min-h-screen md:grid md:grid-cols-[220px_1fr] lg:grid-cols-[240px_1fr] grain relative">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-30 h-14 border-b border-border bg-card px-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => setDrawer(true)} className="w-9 h-9 grid place-items-center border border-border rounded-sm" data-testid="mobile-menu-btn"><List size={18} /></button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 border border-primary/60 grid place-items-center bg-primary/10"><WifiHigh size={14} weight="bold" className="text-primary" /></div>
            <div className="font-display font-bold text-sm">SNS RADIUS</div>
          </div>
          {tenantNas && (
            <span data-testid="tenant-badge-mobile" className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 border border-primary/40 text-primary bg-primary/10">
              <CellTower size={10} /> {tenantNas.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button className="w-9 h-9 grid place-items-center border border-border rounded-sm" data-testid="notif-btn-mobile"><Bell size={14} /></button>
        </div>
      </div>

      {/* Mobile drawer overlay */}
      {drawer && <div onClick={close} className="fixed inset-0 z-40 bg-black/60 md:hidden" />}

      {/* Sidebar */}
      <aside className={`fixed md:static top-0 left-0 z-50 w-[260px] md:w-auto h-screen md:h-auto border-r border-border bg-card md:sticky md:top-0 md:h-screen flex flex-col md:overflow-y-auto transition-transform duration-200 ${drawer ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <div className="p-4 md:p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 border border-primary/60 grid place-items-center bg-primary/10">
              <WifiHigh size={18} weight="bold" className="text-primary" />
            </div>
            <div>
              <div className="font-display font-bold text-sm leading-none">SNS RADIUS</div>
              <div className="uppercase-label mt-1">ISP CONSOLE</div>
            </div>
          </div>
          <button onClick={close} className="md:hidden text-muted-foreground" data-testid="mobile-menu-close"><X size={16} /></button>
        </div>

        <nav className="flex-1 py-2 md:py-3 overflow-y-auto">
          {nav.filter((item) => canSee(item, classify(user))).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={close}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors duration-150 ${
                  isActive
                    ? "border-primary bg-primary/8 text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/40"
                }`
              }
            >
              <item.icon size={16} weight="regular" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-secondary border border-border grid place-items-center font-mono text-xs">
              {(user?.name || "A").slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs truncate">{user?.name}</div>
              <div className="uppercase-label truncate">{user?.role}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            data-testid="change-password-btn"
            onClick={() => setPwdOpen(true)}
            className="w-full rounded-sm border-border text-xs h-8 font-normal mb-2"
          >
            <Key size={14} className="mr-2" /> Change password
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="logout-btn"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full rounded-sm border-border text-xs h-8 font-normal"
          >
            <SignOut size={14} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-col min-w-0">
        <HealthStrip />
        <header className="hidden md:flex border-b border-border h-14 items-center justify-between px-4 md:px-6 bg-background/80 backdrop-blur sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <span className="pulse-dot online" />
            <span className="uppercase-label hidden sm:inline">All systems nominal</span>
            {tenantNas && (
              <div className="ml-3 inline-flex items-center gap-2 pl-3 border-l border-border" data-testid="tenant-badge">
                <CellTower size={14} className="text-primary" />
                <div className="leading-tight">
                  <div className="text-xs font-display font-bold">{tenantNas.name}</div>
                  <div className="uppercase-label text-[9px]">
                    {tenantNas.location || tenantNas.nas_ip}
                    {tenantNas.nas_type && <span className="text-muted-foreground"> · {tenantNas.nas_type}</span>}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="font-mono text-xs text-muted-foreground hidden sm:block">
              {new Date().toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </div>
            <button className="w-8 h-8 grid place-items-center border border-border rounded-sm hover:bg-accent transition-colors" data-testid="notif-btn">
              <Bell size={14} />
            </button>
            <ThemeToggle />
          </div>
        </header>
        <main className="p-3 sm:p-4 md:p-6 relative z-[2] min-h-[calc(100vh-7rem)]">
          <Outlet />
        </main>
        <footer className="border-t border-border px-4 md:px-6 py-3 text-[11px] font-mono text-muted-foreground flex items-center justify-between bg-card/50" data-testid="app-footer">
          <div>© {new Date().getFullYear()} SNS RADIUS</div>
          <div>Powered by <span className="text-primary font-semibold">SNS Technologies</span></div>
        </footer>
      </div>
      <PwaInstallPrompt />

      {/* Change password modal */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="change-password-modal">
          <DialogHeader><DialogTitle className="font-display">Change master password</DialogTitle></DialogHeader>
          <form onSubmit={submitPassword} className="space-y-3">
            <div className="text-[11px] font-mono text-muted-foreground bg-secondary/40 border border-border p-2">
              Current password is your <b>login</b> password. On the live server this is the one set for <code>snsradius@gmail.com</code> in the users collection — not the seed shown on login screen.
            </div>
            <div>
              <Label className="uppercase-label">Current password</Label>
              <Input type="password" required data-testid="pwd-current" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label className="uppercase-label">New password</Label>
              <Input type="password" required data-testid="pwd-new" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} className="mt-1.5 font-mono" placeholder="min 8 characters" />
            </div>
            <div>
              <Label className="uppercase-label">Confirm new password</Label>
              <Input type="password" required data-testid="pwd-confirm" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="mt-1.5 font-mono" />
            </div>
            <div className="text-[10px] text-muted-foreground italic">You'll stay signed in on this device but will need the new password on other sessions.</div>
            <Button type="submit" disabled={pwdSaving} data-testid="pwd-submit" className="w-full rounded-sm bg-primary hover:bg-primary/90">{pwdSaving ? "Saving…" : "Change password"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
